/**
 * Returns true when all visible HealthyHome eligibility questions have been answered.
 *
 * This mirrors isHeaEligibilityComplete but omits the gasProvider and
 * electricProvider questions — those are not asked on the HealthyHome path.
 * `howHeated` is therefore the final question.
 *
 * Single source of truth for HealthyHome eligibility completeness.
 * Both HealthyHome.jsx (rendering) and App.jsx (button-enable logic) import this.
 *
 * @param {Object} values - Current form values
 * @returns {boolean} true when every visible question is answered and the user may proceed
 */
const isHealthyHomeEligibilityComplete = (values = {}) => {
  if (!values.twoYears) return false

  if (!values.paymentAssistance) return false

  if (!values.singleOrMulti) return false

  const ownerOrRenterVisible =
    values.singleOrMulti &&
    !['Single family', '5+ Multi-Family'].includes(values.singleOrMulti)
  if (ownerOrRenterVisible && !values.ownerOrRenter) return false

  const isCondoVisible =
    values.ownerOrRenter ||
    (values.singleOrMulti && ['Single family'].includes(values.singleOrMulti))
  if (isCondoVisible && !values.isCondo) return false

  const multiUnitTypes = ['2-Unit Multi-Family', '3-Unit Multi-Family', '4-Unit Multi-Family']
  const familySizeVisible =
    values.isCondo && values.singleOrMulti && multiUnitTypes.includes(values.singleOrMulti)
  if (familySizeVisible && (values.familySize == null || values.familySize === '')) return false

  const unitNumbersVisible =
    values.isCondo &&
    ((values.singleOrMulti === 'Single family' && values.isCondo === 'yes') ||
      (multiUnitTypes.includes(values.singleOrMulti) &&
        ['1', '2', '3', '4'].includes(String(values.familySize))))

  if (unitNumbersVisible) {
    const unitCount =
      values.singleOrMulti === 'Single family' ? 1 : Number(values.familySize)
    for (let k = 1; k <= unitCount; k++) {
      const unitNumber = values[`unitNumber${k}`]
      if (unitNumber == null || (typeof unitNumber === 'string' && unitNumber.trim() === ''))
        return false
    }
  }

  const lastFilledUnitNumber =
    values.singleOrMulti === 'Single family'
      ? values.unitNumber1
      : values[`unitNumber${Number(values.familySize)}`]

  const howHeatedVisible =
    values.isCondo &&
    ((values.singleOrMulti === 'Single family'
      ? values.isCondo === 'yes'
        ? lastFilledUnitNumber && String(lastFilledUnitNumber).trim().length
        : true
      : false) ||
      (['1', '2', '3', '4'].includes(String(values.familySize)) &&
        lastFilledUnitNumber &&
        String(lastFilledUnitNumber).trim().length))

  const howHeatedEmpty =
    values.howHeated == null ||
    values.howHeated === '' ||
    (typeof values.howHeated === 'string' && values.howHeated.trim() === '')

  if (howHeatedVisible && howHeatedEmpty) return false

  // howHeated is the final question for HealthyHome — no gasProvider or electricProvider checks
  return true
}

export default isHealthyHomeEligibilityComplete
